import React, { useEffect, useState } from 'react';
import { Avatar, Box, LinearProgress, Typography } from '@mui/material';
import { Restaurant as RestaurantIcon } from '@mui/icons-material';
import { getImageUrl } from '../../services/api';
import { toPersianNumber } from '../../utils/persianNumber';
import { M, Tone, formatClock, toneColors } from './monitorTokens';

// Every meal name on this screen is shown with its photo (project rule)
export const MealThumb: React.FC<{ imageUrl?: string | null; title?: string; size?: number }> = ({ imageUrl, title, size = 44 }) => (
    <Avatar
        variant="rounded"
        src={imageUrl ? getImageUrl(imageUrl) : undefined}
        alt={title || ''}
        sx={{ width: size, height: size, borderRadius: '10px', bgcolor: M.surfaceHover, color: M.textFaint, flexShrink: 0 }}
    >
        <RestaurantIcon sx={{ fontSize: size * 0.5 }} />
    </Avatar>
);

export const StatusPill: React.FC<{ tone: Tone; icon?: React.ReactNode; children: React.ReactNode }> = ({ tone, icon, children }) => {
    const c = toneColors(tone);
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 1.25, py: 0.5, borderRadius: '999px',
                bgcolor: c.soft, color: c.text, border: `1px solid ${c.main}40`,
                fontFamily: M.font, fontSize: '0.85rem', fontWeight: 700, whiteSpace: 'nowrap',
                '& svg': { fontSize: '1rem' },
            }}
        >
            {icon}
            {children}
        </Box>
    );
};

// Owns its own one-second timer so the rest of the screen does not re-render every tick
export const LiveClock: React.FC = () => {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const date = new Intl.DateTimeFormat('fa-IR', { weekday: 'long', day: 'numeric', month: 'long' }).format(now);
    return (
        <Box sx={{ textAlign: 'center', lineHeight: 1 }} aria-label="ساعت و تاریخ">
            <Typography sx={{ fontFamily: M.font, fontWeight: 800, fontSize: '1.9rem', color: M.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                {formatClock(now, true)}
            </Typography>
            <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textMuted }}>{date}</Typography>
        </Box>
    );
};

export const KpiTile: React.FC<{
    label: string;
    value: number;
    tone: Tone | 'neutral';
    icon: React.ReactNode;
    progress?: number;
    caption?: string;
}> = ({ label, value, tone, icon, progress, caption }) => {
    const c = tone === 'neutral' ? { main: M.primaryText, text: M.text, soft: M.primarySoft } : toneColors(tone);
    return (
        <Box
            sx={{
                flex: 1, minWidth: 170, bgcolor: M.surface, border: `1px solid ${M.border}`,
                borderRadius: M.radiusCard, boxShadow: M.cardShadow, px: 2.25, py: 1.75,
                display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.75,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontFamily: M.font, color: M.textMuted, fontWeight: 600, fontSize: '0.95rem' }}>{label}</Typography>
                <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: c.soft, color: c.main, display: 'grid', placeItems: 'center' }}>
                    {icon}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{ fontFamily: M.font, fontWeight: 900, fontSize: '2.4rem', lineHeight: 1, color: tone === 'neutral' ? M.text : c.text, fontVariantNumeric: 'tabular-nums' }}>
                    {toPersianNumber(value)}
                </Typography>
                {caption && <Typography sx={{ fontFamily: M.font, color: M.textFaint, fontSize: '0.9rem' }}>{caption}</Typography>}
            </Box>
            {progress !== undefined && (
                <LinearProgress
                    variant="determinate"
                    value={Math.max(0, Math.min(100, progress))}
                    aria-label={`${label} ${toPersianNumber(Math.round(progress))} درصد`}
                    sx={{ height: 6, borderRadius: 3, bgcolor: M.surfaceHover, '& .MuiLinearProgress-bar': { bgcolor: c.main, borderRadius: 3 } }}
                />
            )}
        </Box>
    );
};
